import type { ReactNode } from 'react';
import {
    ArrowTrendingDownIcon,
    ArrowTrendingUpIcon,
    ChatBubbleLeftRightIcon,
    InboxIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { StatCard } from '../../billing/StatusCards';
import type { CommunicationsSummary } from '../../../services/communicationsSummary';

// Three of four cards are filters as well as counts (click to narrow the
// table, click again to clear). "Logged this week" is read-only.

export type CommunicationStat = 'all' | 'open' | 'mine' | 'unassigned';

interface CommunicationsStatCardsProps {
    summary: CommunicationsSummary | null;
    activeStat: CommunicationStat;
    onChange: (stat: CommunicationStat) => void;
}

// Non-interactive twin of StatCard: same layout and accents, never a
// <button>, so it carries no hover affordance implying a click.
function ReadOnlyStatCard({
    label,
    icon,
    value,
    unit,
    unitTone = 'muted'
}: {
    label: string;
    icon: ReactNode;
    value: string;
    unit: ReactNode;
    unitTone?: 'muted' | 'up' | 'down';
}) {
    const unitClass =
        unitTone === 'up'
            ? 'text-emerald-600'
            : unitTone === 'down'
              ? 'text-red-500'
              : 'text-gray-400';
    return (
        <div className="flex-1 min-w-0 flex flex-col justify-center px-5 py-3.5 rounded-xl border-[1.5px] border-gray-200 bg-white shadow-sm text-left">
            <div className="flex items-center gap-2 mb-1">
                <span className="flex-shrink-0 text-gray-400">{icon}</span>
                <span className="text-[11px] font-semibold tracking-[0.04em] uppercase text-gray-400">
                    {label}
                </span>
            </div>
            <div className="flex items-baseline gap-1.5 pl-[22px] min-w-0">
                <span className="text-[26px] font-bold leading-none tabular-nums flex-shrink-0 text-gray-800">
                    {value}
                </span>
                <span className={`text-xs font-medium truncate ${unitClass}`}>{unit}</span>
            </div>
        </div>
    );
}

/** "—" while loading, or when the server could not resolve the viewer. */
function count(value: number | null | undefined): string {
    return value == null ? '—' : String(value);
}

// Absolute change against the previous 7 days. Reads "same as last week"
// rather than "+0" when there is nothing to compare against.
function weekDelta(summary: CommunicationsSummary | null): {
    label: string;
    tone: 'up' | 'down' | 'muted';
} {
    if (!summary) return { label: 'this week', tone: 'muted' };
    const delta = summary.week.total - summary.priorWeekTotal;
    if (delta === 0) return { label: 'same as last week', tone: 'muted' };
    return {
        label: `${delta > 0 ? '+' : ''}${delta} vs last week`,
        tone: delta > 0 ? 'up' : 'down'
    };
}

export function CommunicationsStatCards({
    summary,
    activeStat,
    onChange
}: CommunicationsStatCardsProps) {
    const delta = weekDelta(summary);
    const TrendIcon = delta.tone === 'down' ? ArrowTrendingDownIcon : ArrowTrendingUpIcon;

    return (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 shrink-0">
            <StatCard
                label="Open threads"
                icon={<ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />}
                value={count(summary?.unresolved)}
                unit={`${count(summary?.resolved)} resolved`}
                isActive={activeStat === 'open'}
                onClick={() => onChange('open')}
            />
            <StatCard
                label="Assigned to me"
                icon={<UserIcon className="h-3.5 w-3.5" />}
                value={count(summary?.assignedToMeOpen)}
                unit="still open"
                isActive={activeStat === 'mine'}
                onClick={() => onChange('mine')}
            />
            <StatCard
                label="Unassigned"
                icon={<InboxIcon className="h-3.5 w-3.5" />}
                value={count(summary?.unassignedOpen)}
                unit="nobody owns these"
                isActive={activeStat === 'unassigned'}
                onClick={() => onChange('unassigned')}
            />
            <ReadOnlyStatCard
                label="Logged this week"
                icon={<TrendIcon className="h-3.5 w-3.5" />}
                value={count(summary?.week.total)}
                unit={delta.label}
                unitTone={delta.tone}
            />
        </div>
    );
}
